import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isSameMonth
} from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatDateKey } from '../utils/helpers';

function toDateKey(date) {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Calendar picker that only allows selecting days present in availableDates (YYYY-MM-DD).
 */
const LoadDatePicker = ({
  value,
  onChange,
  availableDates = [],
  allowEmpty = false,
  disabled = false,
  loading = false,
  placeholder,
  className = ''
}) => {
  const { i18n, t } = useTranslation();
  const locale = i18n.language === 'fr' ? fr : enUS;
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const allowed = useMemo(() => new Set(availableDates), [availableDates]);

  const [viewMonth, setViewMonth] = useState(() => {
    if (value) return parseISO(value);
    if (availableDates[0]) return parseISO(availableDates[0]);
    return new Date();
  });

  useEffect(() => {
    if (value) {
      setViewMonth(parseISO(value));
    }
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const padStart = (monthStart.getDay() + 6) % 7;
  const cells = [...Array(padStart).fill(null), ...daysInMonth];

  const weekdayLabels =
    i18n.language === 'fr'
      ? ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di']
      : ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  const displayText = value
    ? formatDateKey(value)
    : placeholder || t('reports.selectDate', 'Choisir une date');

  const hasAllowedInMonth = daysInMonth.some((d) => allowed.has(toDateKey(d)));

  const pick = (dayKey) => {
    onChange(dayKey);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <CalendarIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5 pointer-events-none z-10" />
      <button
        type="button"
        disabled={disabled || loading || (!allowEmpty && availableDates.length === 0)}
        onClick={() => setOpen((prev) => !prev)}
        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white text-left text-gray-900 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
      >
        {loading ? t('common.loading', 'Chargement…') : displayText}
      </button>

      {open && !disabled && !loading && (
        <div className="absolute z-50 mt-1 p-3 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[280px]">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setViewMonth((m) => subMonths(m, 1))}
              className="p-1 rounded hover:bg-gray-100"
              aria-label={t('common.previous', 'Précédent')}
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-700" />
            </button>
            <span className="text-sm font-medium text-gray-900 capitalize">
              {format(viewMonth, 'MMMM yyyy', { locale })}
            </span>
            <button
              type="button"
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              className="p-1 rounded hover:bg-gray-100"
              aria-label={t('common.next', 'Suivant')}
            >
              <ChevronRightIcon className="h-5 w-5 text-gray-700" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-500 mb-1">
            {weekdayLabels.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, idx) => {
              if (!day) {
                return <div key={`pad-${idx}`} className="h-8" />;
              }
              const dayKey = toDateKey(day);
              const enabled = allowed.has(dayKey);
              const selected = value === dayKey;
              const inMonth = isSameMonth(day, viewMonth);

              return (
                <button
                  key={dayKey}
                  type="button"
                  disabled={!enabled}
                  onClick={() => enabled && pick(dayKey)}
                  className={[
                    'h-8 text-sm rounded transition-colors',
                    inMonth ? '' : 'text-gray-300',
                    enabled
                      ? selected
                        ? 'bg-blue-600 text-white font-semibold'
                        : 'text-gray-900 hover:bg-blue-100'
                      : 'text-gray-300 cursor-not-allowed'
                  ].join(' ')}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>

          {!hasAllowedInMonth && (
            <p className="text-xs text-amber-700 mt-2 text-center">
              {t('reports.noLoadDatesInMonth', 'Aucun pesage ce mois-ci')}
            </p>
          )}

          {allowEmpty && (
            <button
              type="button"
              className="mt-2 w-full text-xs text-gray-600 hover:text-gray-900 underline"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              {t('common.clear', 'Effacer')}
            </button>
          )}
        </div>
      )}

      {!loading && availableDates.length === 0 && (
        <p className="text-xs text-amber-700 mt-1">
          {t('reports.noLoadDates', 'Aucune date avec pesage dans la période affichée')}
        </p>
      )}
    </div>
  );
};

export default LoadDatePicker;
